// /app/frontend/src/components/frugal-v2/components/AccountSelector.jsx
// Modal to select account when paying expenses (< 120 lines)

import React, { useState, useEffect } from 'react';
import { Building2, CreditCard, Check, X } from 'lucide-react';
import * as api from '../services/frugalApi';

const AccountSelector = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  amount,
  description,
  token,
  t 
}) => {
  const [accounts, setAccounts] = useState({ banks: [], cards: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchAccounts();
    }
  }, [isOpen, token]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      console.log('[AccountSelector] Fetching accounts with token:', token ? 'present' : 'missing');
      const data = await api.fetchAccounts(token);
      console.log('[AccountSelector] Accounts data:', data);
      setAccounts(data);
      // Pre-select default bank
      const defaultBank = data.banks?.find(b => b.is_default);
      if (defaultBank) setSelectedId(defaultBank.id);
    } catch (err) {
      console.error('[AccountSelector] Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    console.log('[AccountSelector] handleConfirm called, selectedId:', selectedId);
    if (selectedId) {
      const allAccounts = [...accounts.banks, ...accounts.cards];
      const selected = allAccounts.find(a => a.id === selectedId);
      console.log('[AccountSelector] Selected account:', selected);
      if (selected) {
        onSelect(selected);
      } else {
        console.error('[AccountSelector] Account not found for id:', selectedId);
      }
    } else {
      console.log('[AccountSelector] No account selected');
    }
  };

  if (!isOpen) return null;

  const hasAccounts = accounts.banks.length > 0 || accounts.cards.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-xl mx-4 max-w-sm w-full border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {t('frugal.accounts.payWith')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Amount display */}
        <div className="px-4 py-3 bg-white/5 border-b border-white/10">
          <p className="text-slate-400 text-sm">{description}</p>
          <p className="text-white text-xl font-bold">
            ${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>

        {/* Content */}
        <div className="p-4 max-h-80 overflow-y-auto">
          {loading ? (
            <p className="text-center text-slate-400 py-4">{t('frugal.common.loading')}</p>
          ) : !hasAccounts ? (
            <p className="text-center text-slate-400 py-4">
              {t('frugal.accounts.emptyBanks')}
            </p>
          ) : (
            <div className="space-y-4">
              {/* Banks */}
              {accounts.banks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-slate-400">{t('frugal.accounts.banks')}</span>
                  </div>
                  <div className="space-y-2">
                    {accounts.banks.map(bank => (
                      <button
                        key={bank.id}
                        onClick={() => setSelectedId(bank.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                          selectedId === bank.id
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {selectedId === bank.id && <Check className="w-4 h-4 text-emerald-400" />}
                          <span className="text-white">{bank.name}</span>
                          {bank.is_default && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                              {t('frugal.accounts.default')}
                            </span>
                          )}
                        </div>
                        <span className="text-slate-400">${bank.balance?.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cards */}
              {accounts.cards.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-slate-400">{t('frugal.accounts.cards')}</span>
                  </div>
                  <div className="space-y-2">
                    {accounts.cards.map(card => (
                      <button
                        key={card.id}
                        onClick={() => setSelectedId(card.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                          selectedId === card.id
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {selectedId === card.id && <Check className="w-4 h-4 text-amber-400" />}
                          <span className="text-white">{card.name}</span>
                          {card.is_default && (
                            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                              {t('frugal.accounts.default')}
                            </span>
                          )}
                        </div>
                        <span className="text-slate-400">${card.balance?.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg"
          >
            {t('frugal.actions.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedId}
            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
              selectedId
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
          >
            {t('frugal.actions.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSelector;
